import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button
} from '@mui/material';
import { Copy, RotateCcw, Check, AlertTriangle } from 'lucide-react';
import config from '../config';
import authService from '../services/authService';
import { AppContext } from '../App';
import { trackEvent } from '../analytics';

const ProvisioningTokens = () => {
  const { selectedTeam, userRole } = useContext(AppContext);
  const teamId = selectedTeam?.teamId;
  const isAdmin = typeof userRole === 'string' && userRole.toLowerCase() === 'admin';

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [creating, setCreating] = useState(false);

  const [revealedToken, setRevealedToken] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => setShowLoading(true), 150);
    } else {
      setShowLoading(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const fetchTokens = useCallback(async () => {
    if (!teamId || !isAdmin) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/provisioning/tokens?vendorTeamId=${encodeURIComponent(teamId)}`,
        { headers: { ...authService.getAuthHeaders() } }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || `Failed to load tokens (HTTP ${res.status})`);
      }
      setTokens(data.tokens || []);
    } catch (err) {
      console.error('Failed to fetch provisioning tokens:', err);
      setError(err.message || 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  }, [teamId, isAdmin]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const handleCreate = async () => {
    if (!newTokenName.trim()) {
      setError('A name is required.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/provisioning/tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify({ vendorTeamId: teamId, name: newTokenName.trim() })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || `Failed to create token (HTTP ${res.status})`);
      }
      trackEvent('create_provisioning_token');
      setRevealedToken({ token: data.token, name: data.name });
      setCreateOpen(false);
      setNewTokenName('');
      fetchTokens();
    } catch (err) {
      console.error('Failed to create provisioning token:', err);
      setError(err.message || 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (token) => {
    if (!window.confirm(`Revoke token "${token.name}"? External callers using it will stop working immediately.`)) {
      return;
    }
    setError('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/provisioning/tokens/${encodeURIComponent(token.id)}/revoke`,
        { method: 'POST', headers: { ...authService.getAuthHeaders() } }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || `Failed to revoke (HTTP ${res.status})`);
      }
      fetchTokens();
    } catch (err) {
      console.error('Failed to revoke provisioning token:', err);
      setError(err.message || 'Failed to revoke token');
    }
  };

  const copyTokenToClipboard = async () => {
    if (!revealedToken?.token) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(revealedToken.token);
      } else {
        const ta = document.createElement('textarea');
        ta.value = revealedToken.token;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (!teamId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-slate-400">
        Select a team to manage its provisioning tokens.
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-6 text-center">
          <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-2 text-slate-100">Admin access required</h2>
          <p className="text-slate-400 text-sm">
            Only team admins can manage provisioning tokens. Ask an admin on <strong>{selectedTeam.teamName}</strong> to issue one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h2 className="text-2xl font-semibold text-slate-100 mb-2">Provisioning Tokens</h2>
      <p className="text-sm text-slate-400 mb-6 max-w-3xl">
        Tokens let external systems (like a CRM) call <code className="bg-slate-900 px-1.5 py-0.5 rounded text-slate-300">POST /api/provisioning/bundle</code> on behalf of <strong>{selectedTeam.teamName}</strong> to mint demo bundles in one call. Each token is bound to this team and can be revoked at any time.{' '}
        <a href="/docs/provisioning-bundle.html" className="text-indigo-400 hover:text-indigo-300">Learn more →</a>
      </p>

      <button
        onClick={() => {
          setNewTokenName('');
          setError('');
          setCreateOpen(true);
        }}
        className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2 px-4 rounded mb-4"
      >
        Create new token
      </button>

      {error && (
        <div className="bg-red-950/30 border border-red-800/50 text-red-300 px-4 py-3 rounded-lg mb-4" role="alert">
          <strong className="font-semibold">Error:</strong>
          <span className="ml-2">{error}</span>
        </div>
      )}

      {loading && showLoading ? (
        <div className="flex items-center text-slate-400">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mr-3" />
          Loading tokens…
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 overflow-x-auto sm:rounded-lg">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Fingerprint</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Last used</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {tokens.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-slate-500">
                    No tokens yet. Create one to give a CRM or other system access.
                  </td>
                </tr>
              ) : (
                tokens.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-100">{t.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-slate-400">{t.tokenPrefix}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs">
                      {t.isActive ? (
                        <span className="px-2 py-0.5 rounded-full bg-green-900/40 text-green-300 border border-green-800/50">Active</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-red-950/40 text-red-300 border border-red-800/50">Revoked</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">{formatDate(t.createdAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">{formatDate(t.lastUsedAt)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {t.isActive ? (
                        <button
                          onClick={() => handleRevoke(t)}
                          className="text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                          title="Revoke token"
                        >
                          <RotateCcw size={14} />
                          Revoke
                        </button>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>New provisioning token</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Give the token a descriptive name so you know who (or what) it was issued to.
            For example, &ldquo;ZenSlice production&rdquo; or &ldquo;Backup CRM&rdquo;.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Token name"
            fullWidth
            variant="standard"
            value={newTokenName}
            onChange={(e) => setNewTokenName(e.target.value)}
            placeholder="ZenSlice production"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating || !newTokenName.trim()}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!revealedToken}
        onClose={() => setRevealedToken(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Token created — copy it now</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This is the <strong>only time</strong> Táillí will show the full token value.
            Copy it into your CRM (or wherever it's going) before closing this dialog.
            If you lose it, revoke it and create a new one.
          </DialogContentText>
          <div className="bg-slate-950 border border-slate-700 text-green-300 text-xs font-mono p-3 rounded break-all">
            {revealedToken?.token}
          </div>
          <button
            onClick={copyTokenToClipboard}
            className="mt-3 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium py-2 px-3 rounded"
          >
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy to clipboard</>}
          </button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevealedToken(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProvisioningTokens;
